import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { post } from '../lib/api';

interface ApiKey {
  identifier: string;
  key: string;
}

export function ApiKeysPage() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [showGenModal, setShowGenModal] = useState(false);
  const [newId, setNewId] = useState('');

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const data = await post<{ ok: boolean; api_keys: ApiKey[] }>('/panel/apikeys', { action: 'list' });
      setKeys(data.api_keys || []);
    } catch (err: any) {
      if (err.status === 403) {
        toast.error('Access Denied. Only AISc0pez can view this tab.');
      } else {
        toast.error('Failed to load API keys');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newId.trim()) return toast.error('Identifier required');
    try {
      const data = await post<{ ok: boolean; api_keys: ApiKey[] }>('/panel/apikeys', { action: 'generate', identifier: newId.trim() });
      setKeys(data.api_keys);
      setShowGenModal(false);
      setNewId('');
      toast.success('Generated new API key');
    } catch (err: any) {
      toast.error('Failed to generate: ' + err.message);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!confirm(`Are you sure you want to revoke the key for ${id}?`)) return;
    try {
      const data = await post<{ ok: boolean; api_keys: ApiKey[] }>('/panel/apikeys', { action: 'revoke', identifier: id });
      setKeys(data.api_keys);
      toast.success(`Revoked key for ${id}`);
    } catch (err: any) {
      toast.error('Failed to revoke: ' + err.message);
    }
  };

  return (
    <div className="flex flex-col gap-6 animate-fadeIn">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[9px] font-mono tracking-[0.2em] text-slate-600 uppercase mb-1">SC Labs · Owner</div>
          <h1 className="text-2xl font-black uppercase tracking-tight text-white">
            API <span className="text-[#00ffd1] neon-text-glow">Keys</span> Manager
          </h1>
          <p className="text-sm font-mono text-slate-400 mt-1">Manage unique script identifiers for the SC Labs ecosystem.</p>
        </div>
        <button
          onClick={() => setShowGenModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#00ffd1]/10 border border-[#00ffd1]/20 text-[#00ffd1] text-xs font-bold uppercase tracking-wider hover:bg-[#00ffd1]/20 transition-all shadow-[0_0_15px_rgba(0,255,209,0.1)]"
        >
          <i className="fa-solid fa-plus text-xs" />
          Generate New Key
        </button>
      </div>

      <div className="glass-card rounded-2xl border border-slate-800/50 overflow-hidden relative group">
        <div className="absolute inset-0 bg-gradient-to-br from-[#00ffd1]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-800/50 bg-[#070910]/50 backdrop-blur-md">
                <th className="p-4 text-[10px] font-mono text-slate-500 uppercase tracking-widest font-semibold">Identifier</th>
                <th className="p-4 text-[10px] font-mono text-slate-500 uppercase tracking-widest font-semibold">Secret Key</th>
                <th className="p-4 text-[10px] font-mono text-slate-500 uppercase tracking-widest font-semibold w-24 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {loading ? (
                <tr>
                  <td colSpan={3} className="p-8 text-center text-slate-500 text-sm font-mono">
                    <i className="fa-solid fa-spinner animate-spin mr-2"></i> Loading API Keys...
                  </td>
                </tr>
              ) : keys.length === 0 ? (
                <tr>
                  <td colSpan={3} className="p-8 text-center text-slate-500 text-sm font-mono">
                    No active API keys found.
                  </td>
                </tr>
              ) : (
                keys.map((k, i) => (
                  <tr key={i} className="hover:bg-slate-800/20 transition-colors group/row">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center border border-slate-700">
                          <i className="fa-solid fa-microchip text-slate-400 text-xs"></i>
                        </div>
                        <span className="font-mono text-sm text-slate-200">{k.identifier}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <code className="font-mono text-xs text-[#00ffd1] bg-[#00ffd1]/10 px-3 py-1.5 rounded-md border border-[#00ffd1]/20">
                          {k.key}
                        </code>
                        <button 
                          onClick={() => { navigator.clipboard.writeText(k.key); toast.success('Key copied!'); }}
                          className="text-slate-500 hover:text-[#00ffd1] transition-colors" title="Copy to clipboard"
                        >
                          <i className="fa-regular fa-copy"></i>
                        </button>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <button 
                        onClick={() => handleRevoke(k.identifier)}
                        className="w-8 h-8 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500 hover:text-white transition-all opacity-0 group-hover/row:opacity-100 flex items-center justify-center mx-auto"
                        title="Revoke Key"
                      >
                        <i className="fa-solid fa-trash-can text-xs"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showGenModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#070910]/80 backdrop-blur-sm animate-fadeIn">
          <div className="glass-card rounded-2xl border border-slate-800/80 p-6 w-full max-w-md shadow-2xl relative">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#00ffd1] to-transparent opacity-50"></div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-black text-white uppercase tracking-wider">
                Generate <span className="text-[#00ffd1]">Key</span>
              </h3>
              <button onClick={() => setShowGenModal(false)} className="text-slate-500 hover:text-white transition-colors">
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <form onSubmit={handleGenerate}>
              <div className="mb-6">
                <label className="block text-[10px] font-mono text-slate-400 uppercase tracking-widest mb-2">Script / Resource Name</label>
                <div className="relative">
                  <i className="fa-solid fa-tag absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
                  <input
                    type="text"
                    value={newId}
                    onChange={(e) => setNewId(e.target.value)}
                    placeholder="e.g. sclabs_economy"
                    className="w-full bg-[#070910] border border-slate-800 rounded-xl py-3 pl-10 pr-4 text-sm text-white focus:outline-none focus:border-[#00ffd1]/50 focus:ring-1 focus:ring-[#00ffd1]/50 transition-all font-mono"
                    autoFocus
                  />
                </div>
                <p className="text-[10px] text-slate-500 font-mono mt-2 pl-1">Unique identifier for the script needing access.</p>
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowGenModal(false)} className="px-5 py-2.5 rounded-xl border border-slate-700 text-slate-300 text-xs font-bold uppercase tracking-wider hover:bg-slate-800 transition-colors">
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2.5 rounded-xl bg-[#00ffd1]/10 border border-[#00ffd1]/30 text-[#00ffd1] text-xs font-bold uppercase tracking-wider hover:bg-[#00ffd1]/20 shadow-[0_0_15px_rgba(0,255,209,0.2)] transition-all">
                  Generate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
